/**
 * PROGRAM LOGIC: Pure functions for program transformations and scheduling.
 */

interface ScheduleParams {
    programId: string;
    userId: string;
    days: any[];
    startDate: string;
    numCycles: number;
}

export const ProgramCompute = {
    /**
     * Calculates the planned events for a program schedule.
     */
    calculateProgramSchedule({ programId, userId, days, startDate, numCycles }: ScheduleParams) {
        const eventsToInsert: any[] = [];
        const start = new Date(startDate);
        const cycleLength = days.length;

        for (let c = 0; c < numCycles; c++) {
            for (const day of days) {
                // Calculate date: Start + (cycle * length) + (Seq - 1)
                const eventDate = new Date(start);
                const totalDaysOffset = (c * cycleLength) + (day.sequence_number - 1);
                eventDate.setDate(start.getDate() + totalDaysOffset);
                
                const dateStr = eventDate.toISOString().split('T')[0];
                const sessions = Array.isArray(day.sessions) ? day.sessions : (day.sessions ? [day.sessions] : []);
                
                if (sessions.length > 0) {
                    eventsToInsert.push({
                        user_id: userId,
                        type: 'PERFORMANCE',
                        status: 'PLANNED',
                        start_time: `${dateStr}T08:00:00`, // Tactical 8AM slot
                        label: day.label,
                        program_day_id: day.id,
                        payload: { 
                            generated_from_program: programId, 
                            cycle_number: c + 1,
                            session_id: sessions[0].id
                        }
                    });
                }
            }
        }
        return eventsToInsert;
    },

    /**
     * Processes raw deep days into a hydrated UI structure with exercise previews.
     */
    processDeepDays(deepDays: any[], history: any[]) {
        return (deepDays || []).map((day: any) => {
            const session = Array.isArray(day.sessions) ? day.sessions[0] : day.sessions;
            const exerciseData: any[] = [];
            if (session && session.blocks) {
                session.blocks.forEach((block: any) => {
                    if (block.block_items) {
                        block.block_items.forEach((item: any) => {
                            if (item.exercise_library?.name) {
                                exerciseData.push({ 
                                    name: item.exercise_library.name, 
                                    target: `${item.target_sets} × ${item.target_reps}`
                                });
                            }
                        });
                    }
                });
            }
            const dayHistory = (history || []).filter((h: any) => h.program_day_id === day.id);
            return { ...day, last_session: dayHistory[0] || null, exercisePreview: exerciseData };
        });
    },

    /**
     * Recommends the next program day based on history.
     */
    calculateRecommendedDay(processedDays: any[], history: any[]) {
        if (!processedDays || processedDays.length === 0) return null;
        if (!history || history.length === 0) return processedDays[0].id;

        const lastGlobalSession = history[0];
        const lastDayIdx = processedDays.findIndex(d => d.id === lastGlobalSession.program_day_id);
        
        if (lastDayIdx === -1) return processedDays[0].id;
        return processedDays[(lastDayIdx + 1) % processedDays.length].id;
    },

    /**
     * Hydrates program details for the editor.
     */
    hydrateProgramForEditor(program: any, deepDays: any[]) {
        const hydratedDays = (deepDays || []).map((d: any) => {
            const session = Array.isArray(d.sessions) ? d.sessions[0] : d.sessions;
            if (!session) return { id: d.id, label: d.label, sequence_number: d.sequence_number, blocks: [] };
            
            const blocks = (session?.blocks || []).sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0));
            return {
                id: d.id, label: d.label, sequence_number: d.sequence_number,
                blocks: blocks.map((b: any) => ({
                    id: b.id, label: b.label, block_type: b.block_type, sort_order: b.sort_order,
                    items: (b.block_items || []).sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0)).map((i: any) => ({
                        id: i.id, name: i.exercise_library.name, target_sets: i.target_sets, target_reps: i.target_reps,
                        target_weight: i.target_weight, target_rpe: i.target_rpe, tempo: i.tempo, metric_type: i.metric_type,
                        sort_order: i.sort_order, set_targets: i.set_targets
                    }))
                }))
            };
        });
        return { name: program.name, days: hydratedDays };
    }
};

/** @deprecated Use ProgramCompute.calculateProgramSchedule */
export const calculateProgramSchedule = ProgramCompute.calculateProgramSchedule;
